import { Injectable, signal } from '@angular/core';
import { PrintData } from './print.service';

@Injectable({
  providedIn: 'root'
})
export class EscPosService {
  private port: string | null = null;
  public isConnected = signal<boolean>(false);
  private proxyUrl = 'http://localhost:8081';

  // Check if WebSerial is supported (Proxy server overrides this need, so we return true)
  public get isSupported(): boolean {
    return true; // Proxy server works on all browsers
  }

  // Request port from user
  public async requestPrinterAccess(): Promise<boolean> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/ports`);
      if (!response.ok) throw new Error('Proxy server not responding');
      
      const data = await response.json();
      if (data.ports && data.ports.length > 0) {
        // Prioritize VIRTUAL_COM_1 (Console Printer) for development
        const virtualPort = data.ports.find((p: any) => p.path === 'VIRTUAL_COM_1');
        this.port = virtualPort ? virtualPort.path : data.ports[0].path;

        this.isConnected.set(true);
        console.log(`Connected to local printer proxy on port: ${this.port}`);
        return true;
      } else {
        alert('No serial/COM ports found by the local print server.');
        return false;
      }
    } catch (err) {
      console.error('Error connecting to local print server:', err);
      alert('Could not connect to the local Print Server. Please ensure it is running.');
      this.isConnected.set(false);
      return false;
    }
  }

  // Auto-connect if already granted
  public async autoConnect(): Promise<boolean> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/ports`);
      if (response.ok) {
        const data = await response.json();
        if (data.ports && data.ports.length > 0) {
          // Prioritize VIRTUAL_COM_1 (Console Printer) for development
          const virtualPort = data.ports.find((p: any) => p.path === 'VIRTUAL_COM_1');
          this.port = virtualPort ? virtualPort.path : data.ports[0].path;
          
          this.isConnected.set(true);
          return true;
        }
      }
    } catch (err) {
      console.warn('Auto-connect failed: local print server might not be running yet.');
    }
    return false;
  }

  public async disconnect(): Promise<void> {
    this.port = null;
    this.isConnected.set(false);
  }

  public async printReceipt(data: PrintData, openDrawer: boolean = false): Promise<boolean> {
    if (!this.port || !this.isConnected()) {
      console.warn('Printer not connected.');
      return false;
    }

    try {
      // Build ESC/POS bytes
      const bytes = this.buildReceiptBytes(data, openDrawer);
      
      // Send to proxy server
      const response = await fetch(`${this.proxyUrl}/api/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          portPath: this.port,
          data: Array.from(bytes) // Convert Uint8Array to normal array for JSON
        })
      });

      if (!response.ok) {
        throw new Error('Print proxy returned an error');
      }

      return true;
    } catch (err) {
      console.error('Error printing receipt via proxy:', err);
      return false;
    }
  }

  private buildReceiptBytes(data: PrintData, openDrawer: boolean): Uint8Array {
    const { bill, cafeAddress, receiptFooter } = data;
    let payload: number[] = [];

    const addText = (text: string) => {
      // Basic ASCII encoding
      for (let i = 0; i < text.length; i++) {
        payload.push(text.charCodeAt(i));
      }
    };
    
    const addLine = (text: string = '') => {
      addText(text + '\n');
    };

    const INIT = [0x1B, 0x40];
    const ALIGN_CENTER = [0x1B, 0x61, 0x01];
    const ALIGN_LEFT = [0x1B, 0x61, 0x00];
    const ALIGN_RIGHT = [0x1B, 0x61, 0x02];
    const BOLD_ON = [0x1B, 0x45, 0x01];
    const BOLD_OFF = [0x1B, 0x45, 0x00];
    const DOUBLE_SIZE = [0x1D, 0x21, 0x11];
    const NORMAL_SIZE = [0x1D, 0x21, 0x00];
    const CUT = [0x1D, 0x56, 0x41, 0x10]; // partial cut
    const KICK_DRAWER = [0x1B, 0x70, 0x00, 0x19, 0xFA];

    // Barcode commands
    const BARCODE_HEIGHT = [0x1D, 0x68, 80]; // height 80 dots
    const BARCODE_WIDTH = [0x1D, 0x77, 3]; // width 3
    const BARCODE_HRI = [0x1D, 0x48, 2]; // print text below barcode

    payload.push(...INIT);
    
    // Header
    payload.push(...ALIGN_CENTER);
    payload.push(...BOLD_ON);
    payload.push(...DOUBLE_SIZE);
    addLine(bill.cafeName || 'Cafe Name');
    payload.push(...NORMAL_SIZE);
    payload.push(...BOLD_OFF);
    
    if (cafeAddress) {
      addLine(cafeAddress);
    }
    addLine('--------------------------------'); // 32 chars standard width
    
    // Order Info
    payload.push(...ALIGN_LEFT);
    payload.push(...BOLD_ON);
    if (bill.type === 'TAKEAWAY') {
      addLine('*** TAKEAWAY ***');
    } else {
      addLine('*** DINE-IN ***');
    }
    payload.push(...BOLD_OFF);
    addLine(`Order #: ${bill.orderNumber}`);
    if (bill.type !== 'TAKEAWAY' && bill.tableNumber) {
      addLine(`Table: ${bill.tableNumber}`);
    }
    if (bill.tokenNumber) {
      payload.push(...BOLD_ON);
      payload.push(...DOUBLE_SIZE);
      addLine(`TOKEN: ${bill.tokenNumber}`);
      payload.push(...NORMAL_SIZE);
      payload.push(...BOLD_OFF);
    }
    addLine(`Date: ${new Date(bill.orderedAt).toLocaleString()}`);
    addLine('--------------------------------');
    
    // Payment Status Banner
    payload.push(...ALIGN_CENTER);
    payload.push(...BOLD_ON);
    if (bill.status === 'COMPLETED') {
      addLine('*** PAID ***');
    } else {
      addLine('*** PAY AT COUNTER ***');
    }
    payload.push(...BOLD_OFF);
    addLine('--------------------------------');
    payload.push(...ALIGN_LEFT);
    
    // Items
    bill.items.forEach(item => {
      const qtyStr = `${item.quantity} x `;
      // Format to match INR since frontend uses INR, but we'll stick to generic or rupees symbol. Wait, the frontend says ₹. The thermal printer ASCII doesn't support ₹ easily without code page changes. We will use 'Rs.' or just the number. The previous code used $. We will use 'Rs.'.
      const priceStr = `Rs.${item.totalPrice.toFixed(2)}`;
      
      const maxNameLen = 32 - qtyStr.length - priceStr.length;
      let name = item.menuItemName;
      if (item.variantName) {
        name += ` [${item.variantName}]`;
      }

      if (name.length <= maxNameLen) {
        const padding = 32 - (qtyStr.length + name.length + priceStr.length);
        const space = ' '.repeat(padding > 0 ? padding : 0);
        addLine(`${qtyStr}${name}${space}${priceStr}`);
      } else {
        // Line 1: Qty x partOfName       $Price
        const firstLineName = name.substring(0, maxNameLen);
        const padding = 32 - (qtyStr.length + firstLineName.length + priceStr.length);
        const space = ' '.repeat(padding > 0 ? padding : 0);
        addLine(`${qtyStr}${firstLineName}${space}${priceStr}`);

        // Remaining text on next lines, indented by qtyStr.length
        let remainingName = name.substring(maxNameLen);
        const indentStr = ' '.repeat(qtyStr.length);
        const wrapLen = 32 - indentStr.length;
        
        while (remainingName.length > 0) {
           const chunk = remainingName.substring(0, wrapLen);
           addLine(`${indentStr}${chunk}`);
           remainingName = remainingName.substring(wrapLen);
        }
      }

      if (item.addons && item.addons.length > 0) {
        item.addons.forEach(addon => {
          const addonStr = `  + ${addon.quantity}x ${addon.name} (@Rs.${addon.price})`;
          const maxAddonLen = 32;
          let remainingAddon = addonStr;
          while (remainingAddon.length > 0) {
            const chunk = remainingAddon.substring(0, maxAddonLen);
            addLine(chunk);
            remainingAddon = remainingAddon.substring(maxAddonLen);
          }
        });
      }

      if (item.notes) {
         // Indent notes
         const indentStr = '  '; // 2 spaces
         const maxNoteLen = 32 - indentStr.length;
         let remainingNotes = '-> ' + item.notes;
         while (remainingNotes.length > 0) {
           const chunk = remainingNotes.substring(0, maxNoteLen);
           addLine(`${indentStr}${chunk}`);
           remainingNotes = remainingNotes.substring(maxNoteLen);
         }
      }
    });
    
    addLine('--------------------------------');
    
    // Totals
    payload.push(...ALIGN_RIGHT);
    addLine(`Subtotal: Rs.${bill.subtotal.toFixed(2)}`);
    if (bill.discountAmount > 0) {
      addLine(`Discount: -Rs.${bill.discountAmount.toFixed(2)}`);
    }
    addLine(`Tax: Rs.${bill.taxAmount.toFixed(2)}`);
    
    payload.push(...BOLD_ON);
    payload.push(...DOUBLE_SIZE);
    addLine(`TOTAL: Rs.${bill.total.toFixed(2)}`);
    payload.push(...NORMAL_SIZE);
    payload.push(...BOLD_OFF);
    
    payload.push(...ALIGN_CENTER);
    addLine('--------------------------------');
    
    // Barcode
    payload.push(...BARCODE_HEIGHT);
    payload.push(...BARCODE_WIDTH);
    payload.push(...BARCODE_HRI);
    const barcodeText = '{B' + bill.orderNumber;
    payload.push(0x1D, 0x6B, 0x49, barcodeText.length);
    addText(barcodeText);
    addLine();
    
    if (receiptFooter) {
      addLine(receiptFooter);
    }
    
    // Feed lines before cut
    addLine();
    addLine();
    addLine();
    addLine();
    addLine();
    
    // Cut paper
    payload.push(...CUT);

    // Print Logo on Back Side (2ST Printer Support)
    if (data.printLogoOnBack) {
      // Switch to back side: ESC c 0 1
      const SWITCH_TO_BACK = [0x1B, 0x63, 0x30, 0x01];
      payload.push(...SWITCH_TO_BACK);
      
      // Print NV Logo #1: FS p 1 0
      const PRINT_NV_LOGO = [0x1C, 0x70, 0x01, 0x00];
      payload.push(...ALIGN_CENTER);
      payload.push(...PRINT_NV_LOGO);
      
      // Feed lines and cut again for the back side (if needed, depending on printer settings, 
      // some 2ST printers cut both sides together, but we feed lines to ensure logo clears print head)
      addLine();
      addLine();
      addLine();
      
      // Switch back to front side for the next receipt: ESC c 0 0
      const SWITCH_TO_FRONT = [0x1B, 0x63, 0x30, 0x00];
      payload.push(...SWITCH_TO_FRONT);
    }
    
    // Kick drawer
    if (openDrawer) {
      payload.push(...KICK_DRAWER);
    }

    return new Uint8Array(payload);
  }

  public async printKitchenTicket(data: PrintData): Promise<boolean> {
    if (!this.port || !this.isConnected()) {
      return false;
    }

    try {
      const bytes = this.buildKitchenTicketBytes(data);
      const response = await fetch(`${this.proxyUrl}/api/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          portPath: this.port,
          data: Array.from(bytes)
        })
      });

      if (!response.ok) {
        throw new Error('Print proxy returned an error');
      }

      return true;
    } catch (err) {
      console.error('Error printing kitchen ticket via proxy:', err);
      return false;
    }
  }

  private buildKitchenTicketBytes(data: PrintData): Uint8Array {
    const { bill } = data;
    let payload: number[] = [];

    const addText = (text: string) => {
      // Basic ASCII encoding
      for (let i = 0; i < text.length; i++) {
        payload.push(text.charCodeAt(i));
      }
    };
    
    const addLine = (text: string = '') => {
      addText(text + '\n');
    };

    const INIT = [0x1B, 0x40];
    const ALIGN_CENTER = [0x1B, 0x61, 0x01];
    const ALIGN_LEFT = [0x1B, 0x61, 0x00];
    const BOLD_ON = [0x1B, 0x45, 0x01];
    const BOLD_OFF = [0x1B, 0x45, 0x00];
    const DOUBLE_SIZE = [0x1D, 0x21, 0x11];
    const NORMAL_SIZE = [0x1D, 0x21, 0x00];
    const CUT = [0x1D, 0x56, 0x41, 0x10]; // partial cut

    payload.push(...INIT);
    
    // Header
    payload.push(...ALIGN_CENTER);
    payload.push(...BOLD_ON);
    payload.push(...DOUBLE_SIZE);
    addLine('KITCHEN TICKET');
    payload.push(...NORMAL_SIZE);
    payload.push(...BOLD_OFF);
    
    addLine('--------------------------------');
    
    // Order Info
    payload.push(...ALIGN_LEFT);
    payload.push(...BOLD_ON);
    if (bill.type === 'TAKEAWAY') {
      addLine('*** TAKEAWAY ***');
    } else {
      addLine('*** DINE-IN ***');
    }
    payload.push(...BOLD_OFF);
    addLine(`Order #: ${bill.orderNumber}`);
    if (bill.type !== 'TAKEAWAY' && bill.tableNumber) {
      payload.push(...BOLD_ON);
      payload.push(...DOUBLE_SIZE);
      addLine(`Table: ${bill.tableNumber}`);
      payload.push(...NORMAL_SIZE);
      payload.push(...BOLD_OFF);
    }
    if (bill.tokenNumber) {
      payload.push(...BOLD_ON);
      payload.push(...DOUBLE_SIZE);
      addLine(`TOKEN: ${bill.tokenNumber}`);
      payload.push(...NORMAL_SIZE);
      payload.push(...BOLD_OFF);
    }
    addLine(`Date: ${new Date(bill.orderedAt).toLocaleString()}`);
    addLine('--------------------------------');
    addLine(); // Spacing
    
    // Items
    bill.items.forEach(item => {
      payload.push(...BOLD_ON);
      payload.push(...DOUBLE_SIZE);
      const qtyStr = `${item.quantity}x `;
      
      // We can safely truncate or wrap the item name, but for a kitchen ticket we just print it.
      // A thermal printer is ~32 chars wide, DOUBLE_SIZE makes it 16 chars wide.
      // Wrap it safely if it's too long.
      let nameStr = `${qtyStr}${item.menuItemName}`;
      if (item.variantName) {
        nameStr += ` [${item.variantName}]`;
      }
      while(nameStr.length > 0) {
        addLine(nameStr.substring(0, 16));
        nameStr = nameStr.substring(16);
      }
      
      payload.push(...NORMAL_SIZE);
      payload.push(...BOLD_OFF);

      if (item.addons && item.addons.length > 0) {
        item.addons.forEach(addon => {
          addLine(`  + ${addon.quantity}x ${addon.name}`);
        });
      }

      if (item.notes) {
         addLine(`  -> ${item.notes}`);
      }
      addLine(); // Spacing between items
    });
    
    addLine('--------------------------------');
    
    // Feed lines before cut
    addLine();
    addLine();
    addLine();
    addLine();
    addLine();
    
    // Cut paper
    payload.push(...CUT);
    
    return new Uint8Array(payload);
  }
}
